const WINDOW_NOT_FOUND = 'WINDOW_NOT_FOUND';

type WindowNotFoundDetails = {
  reason?: unknown;
  windowHandle?: unknown;
};

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  return value as Record<string, unknown>;
};

const detailsFromError = (error: unknown): WindowNotFoundDetails | undefined => {
  const obj = asRecord(error);
  return obj ? asRecord(obj.details) : undefined;
};

export const isWindowNotFoundError = (error: unknown): boolean =>
  detailsFromError(error)?.reason === WINDOW_NOT_FOUND;

const requestFromPayload = (
  payload?: Record<string, unknown>
): Record<string, unknown> | undefined => asRecord(payload?.request);

const windowHandleFromScoped = (scoped: unknown): number | undefined => {
  const obj = asRecord(scoped);
  if (obj?.mode !== 'window') return undefined;
  return typeof obj.windowHandle === 'number' ? obj.windowHandle : undefined;
};

export const windowHandleFromWindowNotFound = (
  error: unknown,
  payload?: Record<string, unknown>
): number | undefined => {
  const details = detailsFromError(error);
  if (details?.reason !== WINDOW_NOT_FOUND) return undefined;
  if (typeof details.windowHandle === 'number') return details.windowHandle;
  const request = requestFromPayload(payload);
  return (
    windowHandleFromScoped(request?.target) ?? windowHandleFromScoped(request?.scope)
  );
};

export type RpaWindowNotFoundListener = (
  handle: number,
  generation: number | undefined
) => void;

const windowNotFoundListeners = new Set<RpaWindowNotFoundListener>();

let currentEhrPinGeneration: number | null = null;

export const reportEhrPinGeneration = (generation: number | null): void => {
  currentEhrPinGeneration = generation;
};

export const readEhrPinGeneration = (): number | undefined =>
  currentEhrPinGeneration ?? undefined;

export const subscribeRpaWindowNotFound = (
  cb: RpaWindowNotFoundListener
): (() => void) => {
  windowNotFoundListeners.add(cb);
  return () => {
    windowNotFoundListeners.delete(cb);
  };
};

/**
 * Tell every subscriber a pinned window is gone. `run_flow` reports window
 * loss inside its result (`abortReason: 'window_lost'`) rather than as a
 * rejected command, so it cannot ride `notifyRpaWindowNotFound`'s error
 * path; the EHR pin must still be invalidated the same way.
 */
export const notifyRpaWindowLost = (windowHandle: number, generation?: number): void => {
  for (const listener of [...windowNotFoundListeners]) {
    listener(windowHandle, generation);
  }
};

/** The pinned handle of a window-scoped target, if the request has one. */
export const windowHandleFromTarget = (target: unknown): number | undefined =>
  windowHandleFromScoped(target);

export const notifyRpaWindowNotFound = (
  error: unknown,
  payload?: Record<string, unknown>,
  generation?: number
): void => {
  const handle = windowHandleFromWindowNotFound(error, payload);
  if (handle === undefined) return;
  notifyRpaWindowLost(handle, generation);
};
